// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyRenameDetails = {
    title: string;
    value: string;
    /** Sent `{ name }` — the one field this popup exists to set. */
    path: string;
    /** `POST` when the name is creating something rather than changing it — a new building. */
    method?: "PATCH" | "POST";
    placeholder?: string;
    /**
     * Handed the response body, for the caller that wants the thing it just created rather than
     * another round trip to go and find it. Most callers only reload and ignore it.
     */
    onDone: (data?: unknown) => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * One short name and nothing more: renaming a file, adding a building, renaming one.
 *
 * It is one field and one keystroke's worth of thought, so it does not deserve a side form —
 * that shape is for creating and editing a record, and opening a whole panel to change a word reads
 * as a bigger decision than it is.
 *
 * Centred, focused, text selected, Enter saves. The same popup will do for anything else that is one
 * short name and nothing more.
 */
export default function RenamePopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { title, value, path, method = "PATCH", placeholder, onDone } = details as TyRenameDetails;
    const inputRef = useRef<HTMLInputElement>(null);

    const [name, setName] = useState(value);
    const { data, loading, error, succeededAt, invoke } = useFetch(path, { method }, false);

    // Open, type, Enter — never reach for the mouse.
    useEffect(() => {
        inputRef.current?.select();
    }, []);

    useEffect(() => {
        if (!succeededAt) return;
        onClose();
        onDone(data);
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = () => {
        const clean = name.trim();
        // Unchanged is not a save — except when the point is to create something with that name.
        if (!clean || loading || (method === "PATCH" && clean === value)) return;
        invoke({ method, data: { name: clean } });
    };

    return (
        <section className="popup-card rename-popup">
            <h2 className="rename-popup__title">{title}</h2>

            <input
                ref={inputRef}
                className="rename-popup__input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && handleSave()}
                placeholder={placeholder ?? t.drive.namePlaceholder}
                disabled={loading}
                autoFocus
            />

            {error && <p className="rename-popup__error">{error}</p>}

            <div className="rename-popup__actions">
                <button type="button" className="rename-popup__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="rename-popup__save" onClick={handleSave} disabled={loading || !name.trim()}>
                    {loading ? t.common.loading : t.common.save}
                </button>
            </div>
        </section>
    );
}
